<template>
  <div id="vehicle-layer"></div>
</template>

<script>
import Vue from "vue";
import {createNamespacedHelpers} from "vuex";
import * as turf from "@turf/turf";
import { db } from "@/firebase/firebaseConfig.js";
import Mapbox from "mapbox-gl";
import {VehicleMixin} from "@/mixins";


const {
  mapMutations: mapMutationsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap
} = createNamespacedHelpers("liveMap");

export default {
  name: "VehicleLayer",
  mixins: [VehicleMixin],
  data() {
    return {
      firestoreVehicleMap: {},
      vehicleSourceId: "VehicleSourceId",
      vehicleLayerId: "VehicleLayerId",
      vehicleAnimationLayerId: "VehicleAnimationLayerId",
      unsubscribes: [],
      markerMap: {},
      timeoutIdMap: {},
      iconNameCacheMap: {},
      vehiclePinAttentionIcon:        require("@/assets/images/vehicles/vehicle_pin_attention.svg"),
      heavyEquipmentPinAttentionIcon: require("@/assets/images/vehicles/heavy_pin_attention.svg"),
    };
  },
  props: {
    map: Object,
    isReady: {type: Boolean, default: false},
    mapEvent: String
  },
  computed: {
    ...mapGettersOfLiveMap([]),
    ...mapStateOfLiveMap({
      selectedVehicle: state => state.selectedVehicle,
    }),
  },
  watch: {
    mapEvent() {
    },
    async isReady() {
      if (this.isReady) {
        await this.addMapSources();
        await this.addAnimationIcons();
        await this.addMapLayers();
        await this.subscribeVehicles();
      }
    },
    firestoreVehicleMap() {
      if (this.isReady) {
        const featureList = Object.values(this.firestoreVehicleMap).map(vehicleInfo => {
          let vehicleFeature = turf.point([vehicleInfo.geo._long, vehicleInfo.geo._lat]);
          vehicleFeature.properties['trackimo_id'] = vehicleInfo.trackimo_id;
          vehicleFeature.properties['label'] = vehicleInfo.label;
          vehicleFeature.properties['icon']  = ( vehicleInfo.type === this.VEHICLE_TYPE.VEHICLE ) ? "mgtve-vehicle" : "mgtve-heavy-equipment";
          vehicleFeature.properties['text-color']  = ( vehicleInfo.type === this.VEHICLE_TYPE.VEHICLE ) ? "#952AC8" : "#F7B000" ;
          vehicleFeature.properties['sort-order'] = 1;
          return vehicleFeature;
        });
        this.map.getSource(this.vehicleSourceId).setData({
          type: "FeatureCollection",
          features: featureList,
        });

      }
    },
    selectedVehicle() {
      if ( this.selectedVehicle ) {
        const vehicle = this.selectedVehicle;
        this.SET_SELECTED_VEHICLE(null);
        this.animationPopup(vehicle);
      }
    },
  },
  methods: {
    ...mapMutationsOfLiveMap([
      "SET_SELECTED_VEHICLE",
    ]),
    async animationPopup(vehicle) {

      // マーク位置へ map.flyTo() で移動。
      const position = this.map.project([vehicle.geo._long , vehicle.geo._lat]);
      const adjustedLngLat = this.map.unproject({
                                                  x: position.x + 300,
                                                  y: position.y
                                                });
      await this.map.flyTo({
        center: [adjustedLngLat.lng, adjustedLngLat.lat],
        speed: 3,
        essential: true,
      });

      // アイコンを一時的に消す
      let featureList = this.map.getSource(this.vehicleSourceId)._data.features;
      const findIndex = featureList.findIndex(feature => feature.properties['trackimo_id'] === vehicle.trackimo_id);
      const origIcon = featureList[findIndex].properties['icon'];
      if ( origIcon ) {
        this.iconNameCacheMap[vehicle.trackimo_id] = origIcon;
      }
      featureList[findIndex].properties['icon'] = "";
      this.map.getSource(this.vehicleSourceId).setData({
        type: "FeatureCollection",
        features: featureList,
      });

      // アイコン強調表示実行
      await this.invokeAttentionScenario(vehicle.trackimo_id, vehicle.type, vehicle.geo._long , vehicle.geo._lat );

      // アイコンの表示を戻す
      featureList = this.map.getSource(this.vehicleSourceId)._data.features;
      featureList.forEach((feature) => {
        feature.properties['sort-order'] = 1;
      });
      featureList[findIndex].properties['sort-order'] = featureList.length + 1; // 選択アイコンを一番前に出すため、大きい値を設定。
      featureList[findIndex].properties['icon'] = this.iconNameCacheMap[vehicle.trackimo_id];
      this.map.getSource(this.vehicleSourceId).setData({
        type: "FeatureCollection",
        features: featureList,
      });
    },
    async invokeAttentionScenario(trackimoId, vehicleType, lng, lat) {

      const displaySec     = 3;      // 強調表示時間
      const positionOffset = [1, 6]; // 強調アイコン起点の調整値

      if ( !this.markerMap[trackimoId] ) {
        // 強調表示するアイコンがオブジェクト化されていなければ初期化。

        const bounceIcon = ( vehicleType === this.VEHICLE_TYPE.VEHICLE ) ? this.vehiclePinAttentionIcon : this.heavyEquipmentPinAttentionIcon;
        const popup = new Mapbox.Popup({
            closeButton: false,
            closeOnClick: true,
            offset: positionOffset,
            anchor: "bottom",
            className: "vehicle-marker-popup",
          })
          .setHTML(`<img src="${bounceIcon}" />`);
        const el = document.createElement('div');
        el.id = `vehicle-marker-${trackimoId}`;

        this.markerMap[trackimoId] = new Mapbox.Marker(el)
          .setLngLat([ lng , lat ])
          .setPopup(popup)
          .addTo(this.map);
      }

      let marker = this.markerMap[trackimoId];
      marker.togglePopup(true);
      await this.sleep(displaySec);
      marker.togglePopup(false);
    },

    /**
     *  ツール: sleep処理(秒).
     */
    sleep(waitSec) {
      return new Promise(function (resolve) {
        setTimeout(function() { resolve() }, waitSec * 1000);
      });
    } ,

    async addMapSources() {
      this.map.addSource(this.vehicleSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        }
      });
    },
    async addMapLayers() {
      this.map.addLayer({
        id: this.vehicleLayerId,
        type: 'symbol',
        source: this.vehicleSourceId,
        layout: {
          "icon-image": "{icon}",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-offset": [1, -25],
          "text-anchor": "top",
          "text-field": "{label}",
          "text-size": 12,
          "text-offset": [0, 0.2],
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          "symbol-sort-key": { "type": "identity", "property": "sort-order" },
        },
        paint: {
          "text-color": { "type": "identity", "property": "text-color" },
          "text-halo-color": "#828282",
          "text-halo-width": 1,
        }
      });
    },

    async addAnimationIcons() {
      this.map.addLayer({
          id: this.vehicleAnimationLayerId,
          type: "symbol",
          source: this.vehicleSourceId,
          maxzoom: 24,
          layout: {
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            "icon-image": "pulsing"
          }
      });
    },

    async subscribeVehicles() {
      const enterpriseId = this.$store.state.auth.user.id;
      let unsubscribe = await db
        .collection(`vehicles/e:${enterpriseId}/trackimo`)
        .where("type", "in", [ this.VEHICLE_TYPE.VEHICLE, this.VEHICLE_TYPE.HEAVY_EQUIPMENT ])
        .onSnapshot(snapshot => {
          // FireStoreからデータ取得
          snapshot.docChanges().forEach(diff => {
            Vue.set(this.firestoreVehicleMap, diff.doc.id, diff.doc.data());
          });
        });
      this.unsubscribes.push(unsubscribe);
    },

    unsubscribeFireStore() {
      this.unsubscribes.forEach(unsubscribe => unsubscribe());
      this.unsubscribes = [];
    },
  },
  beforeDestroy() {
    this.unsubscribeFireStore();
    Object.values(this.markerMap).forEach(marker => marker.remove());
  },
};
</script>

<style lang="scss">
  .vehicle-marker-popup {
    .mapboxgl-popup-content {
      background-color: rgba(0,0,0, 0);
      padding: 0;
      box-shadow: none;

      img {
        width:  50px;
        height: 50px;
      }
    }
    .mapboxgl-popup-tip {
      display: none;
    }
  }
</style>
